import { useEffect, useRef, useCallback, useState } from 'react';
import { debounce, throttle } from 'lodash';

interface PerformanceMetrics {
  renderTime: number;
  updateFrequency: number;
  memoryUsage: number;
  cacheHitRate: number;
  networkLatency: number;
}

interface OptimizationSettings {
  enableLazyLoading: boolean;
  enableVirtualization: boolean;
  updateDebounceMs: number;
  renderThrottleMs: number;
  prefetchEnabled: boolean;
}

export const usePerformanceOptimization = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renderTime: 0,
    updateFrequency: 0,
    memoryUsage: 0,
    cacheHitRate: 0,
    networkLatency: 0
  });

  const [settings, setSettings] = useState<OptimizationSettings>({
    enableLazyLoading: true,
    enableVirtualization: false,
    updateDebounceMs: 300,
    renderThrottleMs: 16,
    prefetchEnabled: true
  });

  const renderCountRef = useRef(0);
  const lastRenderTime = useRef(0);
  const performanceObserver = useRef<PerformanceObserver | null>(null);

  // Performance monitoring
  const startPerformanceMonitoring = useCallback(() => {
    if ('PerformanceObserver' in window) {
      performanceObserver.current = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        
        entries.forEach((entry) => {
          if (entry.entryType === 'measure') {
            setMetrics(prev => ({
              ...prev,
              renderTime: entry.duration
            }));
          }
        });
      });

      performanceObserver.current.observe({ 
        entryTypes: ['measure', 'navigation', 'resource'] 
      });
    }

    // Memory usage monitoring
    const monitorMemory = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        setMetrics(prev => ({
          ...prev,
          memoryUsage: memory.usedJSHeapSize / memory.jsHeapSizeLimit
        }));
      }
    };

    const memoryInterval = setInterval(monitorMemory, 5000);

    return () => {
      if (performanceObserver.current) {
        performanceObserver.current.disconnect();
      }
      clearInterval(memoryInterval);
    };
  }, []);

  // Optimized update function with debouncing
  const createOptimizedUpdater = useCallback((
    updateFunction: (...args: any[]) => void,
    options: { debounce?: number; throttle?: number } = {}
  ) => {
    const { debounce: debounceMs = settings.updateDebounceMs, throttle: throttleMs } = options;

    if (throttleMs) {
      return throttle(updateFunction, throttleMs, { leading: true, trailing: true });
    }

    return debounce(updateFunction, debounceMs, { leading: false, trailing: true });
  }, [settings.updateDebounceMs]);

  // Render performance tracking
  const trackRenderPerformance = useCallback((componentName: string) => {
    const startTime = performance.now();
    renderCountRef.current++;

    return () => {
      const endTime = performance.now();
      const renderDuration = endTime - startTime;
      
      performance.mark(`${componentName}-render-start`);
      performance.mark(`${componentName}-render-end`);
      performance.measure(
        `${componentName}-render`,
        `${componentName}-render-start`,
        `${componentName}-render-end`
      );

      // Update render frequency
      const now = Date.now();
      if (lastRenderTime.current) {
        const frequency = now - lastRenderTime.current;
        setMetrics(prev => ({
          ...prev,
          updateFrequency: frequency
        }));
      }
      lastRenderTime.current = now;
    };
  }, []);

  // Lazy loading helper
  const createLazyLoader = useCallback((
    loadFunction: () => Promise<any>,
    threshold = 0.1
  ) => {
    if (!settings.enableLazyLoading) {
      return loadFunction;
    }

    return () => {
      return new Promise((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect();
                loadFunction().then(resolve);
              }
            });
          },
          { threshold }
        );

        // Return observer to be used with a target element
        return observer;
      });
    };
  }, [settings.enableLazyLoading]);

  // Memory optimization
  const createMemoizedValue = useCallback(
    function<T>(
      computeValue: () => T,
      dependencies: any[],
      maxAge = 30000 // 30 seconds default
    ): T {
      const cache = useRef<{ value: T; timestamp: number; deps: any[] } | null>(null);

      if (!cache.current || 
          Date.now() - cache.current.timestamp > maxAge ||
          !dependencies.every((dep, index) => dep === cache.current?.deps[index])) {
        
        cache.current = {
          value: computeValue(),
          timestamp: Date.now(),
          deps: [...dependencies]
        };
      }

      return cache.current.value;
    }, []);

  // Network optimization
  const optimizedFetch = useCallback(async (
    url: string,
    options: RequestInit = {},
    cacheTime = 300000 // 5 minutes default
  ) => {
    const cacheKey = `${url}-${JSON.stringify(options)}`;
    const cached = sessionStorage.getItem(cacheKey);
    
    if (cached && settings.prefetchEnabled) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < cacheTime) {
        setMetrics(prev => ({
          ...prev,
          cacheHitRate: Math.min(prev.cacheHitRate + 0.1, 1.0)
        }));
        return data;
      }
    }

    const startTime = performance.now();
    try {
      const response = await fetch(url, options);
      const data = await response.json();
      
      const endTime = performance.now();
      setMetrics(prev => ({
        ...prev,
        networkLatency: endTime - startTime,
        cacheHitRate: Math.max(prev.cacheHitRate - 0.05, 0)
      }));

      // Cache successful responses
      if (response.ok && settings.prefetchEnabled) {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      }

      return data;
    } catch (error) {
      const endTime = performance.now();
      setMetrics(prev => ({
        ...prev,
        networkLatency: endTime - startTime
      }));
      throw error;
    }
  }, [settings.prefetchEnabled]);

  // Update optimization settings
  const updateSettings = useCallback((newSettings: Partial<OptimizationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  // Cleanup
  useEffect(() => {
    return startPerformanceMonitoring();
  }, [startPerformanceMonitoring]);

  return {
    metrics,
    settings,
    updateSettings,
    createOptimizedUpdater,
    trackRenderPerformance,
    createLazyLoader,
    createMemoizedValue,
    optimizedFetch
  };
};